import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";

// Novo schema de validação baseado no JSON fornecido
const formSchema = z.object({
  subscriptionYear: z.string().min(1, "por favor, selecione o ano de inscrição"),
  sadnessLevel: z.string().min(1, "por favor, selecione o nível de tristeza"),
  readingFrequency: z.string().min(1, "por favor, selecione a frequência de leitura"),
  trustLevel: z.string().min(1, "por favor, selecione o nível de confiança"),
  readingStyle: z.string().min(1, "por favor, selecione o estilo de leitura"),
  linkInteraction: z.string().min(1, "por favor, selecione como interage com links"),
  saturdayReading: z.string().min(1, "por favor, selecione como é sua leitura aos sábados"),
  theNewsIs: z.string().min(1, "por favor, selecione o que é o the news para você"),
  podcast: z.string().min(1, "por favor, selecione se já ouviu o podcast"),
  videoNews: z.string().min(1, "por favor, responda sobre o jornal em vídeo"),
  videoNewsTime: z.string().min(1, "por favor, selecione o horário para vídeo"),
  videoNewsHabit: z.string().min(1, "por favor, selecione seu hábito de vídeo"),
  languageTone: z.string().min(1, "por favor, selecione sua opinião sobre o tom"),
  impartiality: z.string().min(1, "por favor, selecione sua opinião sobre imparcialidade"),
  email: z.string().email("e-mail inválido").min(1, "e-mail é obrigatório"),
});

type FormValues = z.infer<typeof formSchema>;

// Configurações do formulário
const FORM_CONFIG = {
  color: '#FECF04',
  logo: '/thenews.webp',
  title: 'quanto você gosta e o que você mexeria no the news?'
};

// IDs dos campos conforme o novo JSON
const FIELD_IDS = {
  subscriptionYear: "ysKCQKT90Viv5jxWc-eUe",
  sadnessLevel: "AWAHui4yPpjjwAbtnpNSB",
  readingFrequency: "2DKKb1eRvuco5xiRv6GK3",
  trustLevel: "jEa28Q1GTLUS6hq7CT7Yk",
  readingStyle: "Zd5JmtJMlrE1R6D7MxwCT",
  linkInteraction: "gy69HWeUbBKjHuQaFtD8v",
  saturdayReading: "AHZHxiQ2b5KW-Q4m53Hyh",
  theNewsIs: "nUbcI93dd_lg-G0HD2TEg",
  podcast: "08R33Kqlf6J2w0V3kLel1",
  videoNews: "bYoujG8OO7foNnU7SeZ-R",
  videoNewsHabit: "3_7BNYB-OVOku3a5Hx9SX",
  videoNewsTime: "rj3i-OqRyGMQLX5Xzde80",
  languageTone: "nkZsa-XTqwcrNyAoPtk7e",
  impartiality: "6yAJy3ZHqwN_1asgGD36o",
  email: "WOlyQScE6e8OZ1kmk_1S-",
};

// Adicionar componente de botão de alternativa (compacto)
function OptionButton({ value, selected, onClick, children, className = "" }: { value: string, selected: boolean, onClick: (v: string) => void, children: React.ReactNode, className?: string }) {
  // children esperado: <span className="font-bold mr-2">Letra</span> Texto
  // Vamos separar a letra e o texto
  let letra = null;
  let texto = children;
  if (Array.isArray(children) && children.length === 2 && typeof children[0] === 'object') {
    letra = children[0];
    texto = children[1];
  }
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`flex items-center justify-center px-3 py-2 rounded-lg border text-sm font-sm transition-colors
        ${selected ? 'bg-[var(--newsletter-color)] text-white border-[var(--newsletter-color)]' : 'bg-gray-100 text-gray-900 border-gray-200 hover:bg-[var(--newsletter-color)]/10'}
        ${className}
      `}
      style={{ minHeight: 36 }}
    >
      {/* Letra destacada */}
      {letra && (
        <span className={`flex items-center justify-center w-7 h-7 rounded-md font-bold text-base mr-1
          ${selected ? 'bg-white text-[var(--newsletter-color)]' : 'bg-gray-300 text-gray-700'}`}
        >
          {letra.props.children}
        </span>
      )}
      {/* Texto da resposta */}
      <span className="flex-1 text-left whitespace-pre-line">{texto}</span>
    </button>
  );
}

export function FeedbackForm() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const emailParam = searchParams.get('email') || "";
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      subscriptionYear: "",
      sadnessLevel: "",
      readingFrequency: "",
      trustLevel: "",
      readingStyle: "",
      linkInteraction: "",
      saturdayReading: "",
      theNewsIs: "",
      podcast: "",
      videoNews: "",
      videoNewsTime: "",
      videoNewsHabit: "",
      languageTone: "",
      impartiality: "",
      email: emailParam,
    },
    mode: "onChange"
  });

  // Atualiza o valor do e-mail sempre que o parâmetro mudar
  useEffect(() => {
    form.setValue('email', emailParam);
  }, [emailParam]);

  // Calcula progresso do formulário
  useEffect(() => {
    const values = form.getValues();
    const totalFields = Object.keys(values).length;
    const filledFields = Object.values(values).filter(value => value !== "").length;
    const progress = (filledFields / totalFields) * 100;
    setProgress(progress);
  }, [form.watch()]);

  const onSubmit = async (data: FormValues) => {
    try {
      setLoading(true);
      setError("");

      const formData = {
        formId: "_zH3OSO3y5b-bft82OUBG",
        userId: emailParam,
        metadata: {
          source: "newsletter",
          campaign: "feedback_junho2025"
        },
        values: [
          { fieldId: FIELD_IDS.subscriptionYear, value: data.subscriptionYear },
          { fieldId: FIELD_IDS.sadnessLevel, value: data.sadnessLevel },
          { fieldId: FIELD_IDS.readingFrequency, value: data.readingFrequency },
          { fieldId: FIELD_IDS.trustLevel, value: data.trustLevel },
          { fieldId: FIELD_IDS.readingStyle, value: data.readingStyle },
          { fieldId: FIELD_IDS.linkInteraction, value: data.linkInteraction },
          { fieldId: FIELD_IDS.saturdayReading, value: data.saturdayReading },
          { fieldId: FIELD_IDS.theNewsIs, value: data.theNewsIs },
          { fieldId: FIELD_IDS.podcast, value: data.podcast },
          { fieldId: FIELD_IDS.videoNewsHabit, value: data.videoNewsHabit },
          { fieldId: FIELD_IDS.videoNewsTime, value: data.videoNewsTime },
          { fieldId: FIELD_IDS.languageTone, value: data.languageTone },
          { fieldId: FIELD_IDS.impartiality, value: data.impartiality },
          { fieldId: FIELD_IDS.videoNews, value: data.videoNews },
          { fieldId: FIELD_IDS.email, value: emailParam },
        ]
      };

      const response = await fetch('https://backend.testeswaffle.org/forms/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Erro ao enviar formulário' }));
        throw new Error(errorData.message || 'Erro ao enviar formulário');
      }

      setIsSuccess(true);
      form.reset();
      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erro:', error);
      setError(error instanceof Error ? error.message : 'Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Garante que o valor do e-mail esteja sempre preenchido antes do submit
  const handleSubmit = (values: FormValues) => {
    // Força o valor do e-mail antes do submit
    form.setValue('email', emailParam);
    return onSubmit({ ...values, email: emailParam });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className={`animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[${FORM_CONFIG.color}]`}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center pt-8 relative pb-20" style={{ '--newsletter-color': FORM_CONFIG.color } as React.CSSProperties}>
      <div className="w-full max-w-5xl p-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-1 flex flex-col justify-center text-center"
        >
          <img 
            src={FORM_CONFIG.logo}
            alt={FORM_CONFIG.title}
            className="w-40 mx-auto mb-6"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <h1 className="text-4xl font-bold text-gray-900">
              {FORM_CONFIG.title}
            </h1>

            {isSuccess ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="bg-white rounded-3xl p-12 shadow-sm border border-gray-100 max-w-3xl mx-auto text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="flex items-center justify-center w-20 h-20 rounded-full bg-[var(--newsletter-color)] text-white mx-auto mb-6"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </motion.div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">resposta enviada com sucesso! 🎉</h2>
                <p className="text-xl text-gray-700">
                  obrigado por participar da nossa pesquisa!
                </p>
              </motion.div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 max-w-3xl mx-auto"
                  >
                    <div className="space-y-6">
                      <FormField
                        control={form.control}
                        name="subscriptionYear"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Quando você se inscreveu no the news?</h3>
                            <FormControl>
                              <div>
                                <div className="grid grid-cols-3 md:grid-cols-6 gap-4 w-full justify-items-center">
                                  {["2020", "2021", "2022", "2023", "2024", "2025", "2026"].map((year, idx) => (
                                    <OptionButton
                                      key={year}
                                      value={year}
                                      selected={field.value === year}
                                      onClick={field.onChange}
                                    >
                                      <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {year}
                                    </OptionButton>
                                  ))}
                                </div>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="sadnessLevel"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">De 0 a 10, quanto você ficaria triste se o the news não existisse?</h3>
                            <FormControl>
                              <div>
                                <div className="flex flex-wrap gap-2 justify-center mb-2">
                                  {Array.from({ length: 11 }, (_, i) => i.toString()).map((num) => (
                                    <OptionButton
                                      key={num}
                                      value={num}
                                      selected={field.value === num}
                                      onClick={field.onChange}
                                    >
                                      {num}
                                    </OptionButton>
                                  ))}
                                </div>
                                <div className="flex justify-between text-xs text-gray-500 px-2">
                                  <span>Pouco triste</span>
                                  <span>Tanto faz</span>
                                  <span>Muito triste</span>
                                </div>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="trustLevel"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Você confia no que lê no the news?</h3>
                            <FormControl>
                              <div>
                                <div className="flex flex-wrap gap-2 justify-center mb-2">
                                  {["1", "2", "3", "4", "5"].map((num) => (
                                    <OptionButton
                                      key={num}
                                      value={num}
                                      selected={field.value === num}
                                      onClick={field.onChange}
                                    >
                                      {num}
                                    </OptionButton>
                                  ))}
                                </div>
                                <div className="flex justify-between text-xs text-gray-500 px-2">
                                  <span>Desconfio totalmente</span>
                                  <span>Confio totalmente</span>
                                </div>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="readingFrequency"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Atualmente, com qual frequência você de fato lê (e não só abre) o the news?</h3>
                            <FormControl>
                              <div className="flex flex-col gap-3 w-full">
                                {["Todo dia. Sem falhar nunca.", "Quase todo dia. Média de 4x a 5x na semana.", "Nem lá, nem cá. Costumo ler 2x ou 3x na semana", "Nem sempre. Abro 1x ou 2x na semana.", "Sinceramente? Praticamente 1x a 2x no mês só."].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="readingStyle"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Qual seu estilo de leitura do the news?</h3>
                            <FormControl>
                              <div className="flex flex-col gap-3 w-full">
                                {["Leio sempre tudo muito atentamente, palavra por palavra.", "Leio tudo sem falta, mas passando o olho.", "Só leio as matérias que me interessam. Raramente, leio todas.", "Começo a ler e paro no meio da edição, quando cansa.", "Costumo parar minha leitura quando chega um anúncio.", "É um pouco de tudo isso; depende muito do dia."].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="linkInteraction"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Como você interage com links da edição?</h3>
                            <FormControl>
                              <div className="flex flex-col gap-3 w-full">
                                {["Clico muito. E quase sempre duas ou mais vezes.", "Clico sempre. Muitas vezes só em um link, mas sempre clico.", "Oscila bastante, dependo do dia. Nem sempre clico, mas quando clico, é em vários.", "Quase não clico. Bem raro eu me aprofundar em algo.", "Links? Nunca nem vejo eles na edição."].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="saturdayReading"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Como é sua leitura aos sábados?</h3>
                            <FormControl>
                              <div className="flex flex-col gap-3 w-full">
                                {["Igual aos dias de semana. Nada muda.", "Costuma ser mais lenta, leio com mais calma.", "Só abro edições de sábado na segunda.", "Quase nunca abro edição de sábado, nem mesmo depois.", "Sinceramente? Nem sabia que recebo edições de sábado."].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="theNewsIs"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Para você o the news é...</h3>
                            <FormControl>
                              <div className="flex flex-col gap-3 w-full">
                                {["Um blog moderno para leitura rápida", "Um jornal confiável", "Uma página no Instagram que me mantém atualizado", "Uma newsletter prática e inteligente", "Uma marca de newsletters"].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="podcast"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Você já ouviu o podcast do the news?</h3>
                            <FormControl>
                              <div className="flex flex-col gap-3 w-full">
                                {["Sim, sou fã e escuto todos os dias", "Sim, escuto umas 3x por semana", "Sim, mas prefiro a newsletter", "Não, mas vou experimentar", "Não, não gosto do formato"].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="videoNewsHabit"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Hoje, você costuma consumir notícias em vídeo?</h3>
                            <FormControl>
                              <div className="flex flex-col gap-3 w-full">
                                {["Sim, ao vivo na TV (ex: GloboNews, Jornal Nacional, SBT Brasil, Brasil Urgente, Jornal da Record, Jornal da Cultura, etc.)", "Sim, ao vivo no YouTube (ex: Pingos nos Is, Revista Oeste, CNN Brasil, BandNews, UOL, Estadão, Pânico, etc.)", "Sim, gravado (ex: vídeos de análise no YouTube, Fantástico, GloboNews Painel, Mundo S/A, Cidades e Soluções, UOL News etc.)", "Sim, mas só em vídeos curtos (Reels, TikTok, Shorts etc.)", "Não costumo consumir notícias em vídeo"].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="videoNewsTime"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Em qual horário do dia você acha que assistiria ao the news em vídeo?</h3>
                            <FormControl>
                              <div className="flex flex-col gap-3 w-full">
                                {["De manhã, logo cedo", "No horário do almoço", "No fim da tarde", "À noite"].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="languageTone"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Sobre linguagem e tom do the news (brincadeiras e piadas):</h3>
                            <FormControl>
                              <div className="flex flex-col gap-3 w-full">
                                {["Odeio as piadinhas. Deveria ser mais formal.", "Não gosto das piadinhas, mas poderia ser mais informal.", "Até gosto das piadinhas, mas está demais, deveria ser mais formal.", "Gosto das piadinhas, poderia ser mais informal.", "Amo as piadinhas, está perfeito assim"].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="impartiality"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Sobre imparcialidade:</h3>
                            <FormControl>
                              <div className="flex flex-col gap-3 w-full">
                                {["Eu acho vocês MUITO imparciais.", "Eu acho vocês imparciais.", "Dá para ver que tentam ser imparciais, mas às vezes deslizam para direita.", "Dá para ver que tentam ser imparciais, mas às vezes deslizam para esquerda."].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="videoNews"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Se o the news virasse um jornal em vídeo, o que você esperaria?</h3>
                            <FormControl>
                              <textarea
                                className="w-full border rounded-lg p-2 min-h-[80px]"
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Compartilhe sua opinião..."
                                required
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-[var(--newsletter-color)] hover:bg-[var(--newsletter-color)]/90 text-white px-6 py-4 text-base rounded-lg transition-colors mt-6"
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-4 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        'enviar'
                      )}
                    </Button>
                  </motion.div>

                  <div className="mt-auto flex justify-center items-center space-x-4">
                    <div className="h-2 w-48 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-[var(--newsletter-color)]"
                        initial={{ width: "0%" }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    <span className="text-base md:text-lg text-gray-500">
                      {Math.round(progress)}% completo
                    </span>
                  </div>

                  {error && (
                    <motion.p
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-center mt-2"
                    >
                      {error}
                    </motion.p>
                  )}
                </form>
              </Form>
            )}
          </motion.div>
        </motion.div>

        {/* Barra de Progresso Fixa */}
        <div className="fixed left-0 bottom-0 w-full h-2 bg-gray-200 z-50">
          <motion.div 
            className="h-full bg-[var(--newsletter-color)]"
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>
    </div>
  );
} 